import { useI18n } from '~/app/i18n/i18n';
import { VATConfig, useMarketConfig } from '~/app/market-config';
import { MonthlyRateFragment, PartRateFragment, PriceFragment } from '~/graphql/__generated__/graphql';

export type CurrencyFormatterInput = number | undefined | null | false;
export type CurrencyFormatterDisplayContext = 'standard' | 'min-price';

export function useCurrencyFormatter() {
	const { i18n } = useI18n();
	const { formatter } = useMarketConfig();

	return {
		formatCurrency: (
			price: PriceFragment | null | undefined,
			vatConfig: VATConfig = 'hidden',
			context: CurrencyFormatterDisplayContext = 'standard',
		) => {
			if (!price || price.__typename !== 'Price') {
				return '';
			}

			const value = formatter.formatPrice(price.value);

			if (context === 'min-price') {
				return i18n('ngc.currency.minPriceWithVAT', { value });
			}

			if (vatConfig === 'inclusive') {
				return i18n('ngc.currency.priceWithVAT', { value });
			}

			if (vatConfig === 'exclusive') {
				return i18n('ngc.currency.priceExcludingVAT', { value });
			}

			if (vatConfig === 'hidden') {
				return value;
			}

			return i18n('ngc.currency.priceWithOptionalVAT', { value });
		},
		/**
		 * Provides a formattedValue for a price object.
		 * @example
		 * formatMonthlyRate({ __typename: 'Price', value: 1234.56 })
		 * // => '1.234,56 € (inkl. MwSt.)'
		 */
		formatPriceWithVAT: (price: PriceFragment | null | undefined) => {
			if (!price || price.__typename !== 'Price') {
				return '';
			}

			const value = formatter.formatPrice(price.value);

			return i18n('ngc.currency.priceWithVAT', { value });
		},
		/**
		 * Provides a formattedValue for a monthly rate or part rate.
		 * @example
		 * formatMonthlyRate({ __typename: 'MonthlyRate', rawValue: 1234.56 })
		 * // => '1.234,56 €/Monat (inkl. MwSt)'
		 */
		formatMonthlyRate: (rate: MonthlyRateFragment | PartRateFragment | undefined | null) => {
			if (!rate || rate.__typename === 'FinancingError') {
				return '';
			}

			const value = formatter.formatPrice(rate.rawValue);

			return i18n('ngc.currency.monthlyRate', { value });
		},

		currency: () => {
			return formatter.currency();
		},
	};
}
